import { useEffect, useMemo, useState } from "react";
import GraficaFwi from "./GraficaFwi";
import { useGetFwi } from "../../hooks/useGetFwi";
import { useGetFwiPrediction } from "../../hooks/useGetFwiPrediction";
import { API_URL } from "../../config";
import { authFetch } from "../../utils";
import WaterRequestsList from "./WaterRequestsList";
import ForceWaterRequest from "./ForceWaterRequest";
import useGetWaterRequests from "../../hooks/useGetWaterRequests";
//import CannonActivationList from "./CannonActivationList";

const MAX_RANGE_MS = 90 * 24 * 60 * 60 * 1000;

async function downloadCsv(url, filename) {
  try {
    const res = await authFetch(url);
    if (res.status === 404) {
      alert("No hay datos en el rango solicitado.");
      return;
    }
    if (!res.ok) throw new Error(`Error ${res.status}`);
    const blob = await res.blob();
    const objectUrl = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = objectUrl;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(objectUrl);
  } catch (err) {
    alert(`Error al descargar: ${err.message}`);
  }
}

export default function RiskPage({ isAdmin, canManageCannons }) {
  const waterRequestsHook = useGetWaterRequests();
  const [from, setFrom] = useState(
    () => new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
  );
  const [to, setTo] = useState(() => new Date().toISOString());

  const from30DaysAgo = useMemo(
    () => new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
    []
  );

  const meteoIds = ["torrevallesa", "cannonBravo", "cannonDelta1", "cannonDelta2"];

  const [referenceMeteoOptions, setReferenceMeteoOptions] = useState([]);
  const [referenceMeteoId, setReferenceMeteoId] = useState('');
  const [savingReferenceMeteo, setSavingReferenceMeteo] = useState(false);
  const [referenceMeteoMessage, setReferenceMeteoMessage] = useState('');

  useEffect(() => {
    if (!isAdmin) return;

    authFetch(`${API_URL}meteos/facility/paterna`)
      .then(res => res.ok ? res.json() : [])
      .then(stations => setReferenceMeteoOptions(stations))
      .catch(() => setReferenceMeteoOptions([]));

    authFetch(`${API_URL}fwi/zones`)
      .then(res => res.ok ? res.json() : [])
      .then(zones => {
        const zonaSinCannones = zones.find(z => !z.hasCannons);
        if (zonaSinCannones) setReferenceMeteoId(zonaSinCannones.meteoId);
      })
      .catch(() => {});
  }, [isAdmin]);

  const handleSaveReferenceMeteo = async () => {
    if (!referenceMeteoId) return;

    const selectedName = referenceMeteoOptions.find(s => s.meteoId === referenceMeteoId)?.name || referenceMeteoId;
    const confirmed = window.confirm(
      `A partir de ahora, los cálculos de FWI se harán con los datos de "${selectedName}". ¿Continuar?`
    );
    if (!confirmed) return;

    setSavingReferenceMeteo(true);
    setReferenceMeteoMessage('');
    try {
      const res = await authFetch(`${API_URL}fwi/zones/reference-meteo`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ meteoId: referenceMeteoId })
      });
      if (!res.ok) throw new Error('Error al guardar');
      const data = await res.json();
      setReferenceMeteoMessage(`✅ Actualizado en ${data.zonesModifiedCount} zona(s) y ${data.cannonStationsModifiedCount} estación(es) de cañón`);
    } catch (err) {
      setReferenceMeteoMessage('❌ Error al guardar la estación de referencia');
    } finally {
      setSavingReferenceMeteo(false);
      setTimeout(() => setReferenceMeteoMessage(''), 5000);
    }
  };

  const handleFromChange = (value) => {
    const iso = new Date(value).toISOString();
    if (new Date(to) - new Date(iso) > MAX_RANGE_MS) {
      alert("El intervalo máximo permitido es de 30 días");
      return;
    }
    if (new Date(iso) > new Date(to)) return;
    setFrom(iso);
  };

  const handleToChange = (value) => {
    const iso = new Date(value).toISOString();
    if (new Date(iso) - new Date(from) > MAX_RANGE_MS) {
      alert("El intervalo máximo permitido es de 30 días");
      return;
    }
    if (new Date(iso) < new Date(from)) return;
    setTo(iso);
  };

  const torreVallesaParams = useMemo(() => ({ meteoId: "torrevallesa", from, to }), [from, to]);
  const cannonBravoParams  = useMemo(() => ({ meteoId: "cannonBravo",  from, to }), [from, to]);
  const cannonDelta1Params = useMemo(() => ({ meteoId: "cannonDelta1", from, to }), [from, to]);
  const cannonDelta2Params = useMemo(() => ({ meteoId: "cannonDelta2", from, to }), [from, to]);

  const { observations: torreVallesaObservations, isLoading: isLoadingTorrevallesa } = useGetFwi(torreVallesaParams);
  const { observations: cannonBravo,  isLoading: loadingBravo  } = useGetFwi(cannonBravoParams);
  const { observations: cannonDelta1, isLoading: loadingDelta1 } = useGetFwi(cannonDelta1Params);
  const { observations: cannonDelta2, isLoading: loadingDelta2 } = useGetFwi(cannonDelta2Params);

  const { prediction: torreVallesaPrediction, isLoadingPrediction: isLoadingTorreVallesaPrediction } = useGetFwiPrediction({ meteoId: "torrevallesa" });
  const { prediction: cannonBravoPrediction,  isLoadingPrediction: loadingBravoPred  } = useGetFwiPrediction({ meteoId: "cannonBravo" });
  const { prediction: cannonDelta1Prediction, isLoadingPrediction: loadingDelta1Pred } = useGetFwiPrediction({ meteoId: "cannonDelta1" });
  const { prediction: cannonDelta2Prediction, isLoadingPrediction: loadingDelta2Pred } = useGetFwiPrediction({ meteoId: "cannonDelta2" });

  const isLoadingObservations  = isLoadingTorrevallesa || loadingBravo || loadingDelta1 || loadingDelta2;
  const isLoadingPredictions   = isLoadingTorreVallesaPrediction || loadingBravoPred || loadingDelta1Pred || loadingDelta2Pred;

  return (
    <div style={{ paddingLeft: 30 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 12, marginTop: 24, flexWrap: "wrap" }}>
        <label style={{ fontSize: 13 }}>
          Inicio:&nbsp;
          <input
            type="datetime-local"
            value={from.slice(0, 16)}
            onChange={(e) => handleFromChange(e.target.value)}
          />
        </label>
        <label style={{ fontSize: 13 }}>
          Fin:&nbsp;
          <input
            type="datetime-local"
            value={to.slice(0, 16)}
            onChange={(e) => handleToChange(e.target.value)}
          />
        </label>
      </div>

      <GraficaFwi
        torreVallesaObservations={torreVallesaObservations}
        torreVallesaPrediction={torreVallesaPrediction}
        cannonBravo={cannonBravo}
        cannonBravoPrediction={cannonBravoPrediction}
        cannonDelta1={cannonDelta1}
        cannonDelta1Prediction={cannonDelta1Prediction}
        cannonDelta2={cannonDelta2}
        cannonDelta2Prediction={cannonDelta2Prediction}
        isLoading={isLoadingObservations}
        isLoadingPredictions={isLoadingPredictions}
      />

      <WaterRequestsList hook={waterRequestsHook}/>
      {canManageCannons && (
        <ForceWaterRequest onCreated={waterRequestsHook.refresh} waterRequests={waterRequestsHook.waterRequests} />
      )}

      {isAdmin && (
        <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", padding: 12, border: "1px solid #ddd", borderRadius: 6, marginBottom: 8 }}>
            <label style={{ fontSize: 13 }}>
              Meteo de referencia para cálculo de FWI:&nbsp;
              <select
                value={referenceMeteoId}
                onChange={(e) => setReferenceMeteoId(e.target.value)}
              >
                {referenceMeteoOptions.map((s) => (
                  <option key={s.meteoId} value={s.meteoId}>{s.name}</option>
                ))}
              </select>
            </label>
            <button onClick={handleSaveReferenceMeteo} disabled={savingReferenceMeteo || !referenceMeteoId}>
              {savingReferenceMeteo ? "Guardando..." : "Guardar"}
            </button>
            {referenceMeteoMessage && <span style={{ fontSize: 13 }}>{referenceMeteoMessage}</span>}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {meteoIds.map((meteoId) => (
              <button
                key={`obs-${meteoId}`}
                onClick={() => downloadCsv(
                  `${API_URL}fwi/${meteoId}?from=${from30DaysAgo}&to=${to}&format=csv`,
                  `FWI_${meteoId}_ultimos_30_dias.csv`
                )}
              >
                Descargar {meteoId} últimos 30 días
              </button>
            ))}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {meteoIds.map((meteoId) => (
              <button
                key={`pred-${meteoId}`}
                onClick={() => downloadCsv(
                  `${API_URL}fwi/prediction/${meteoId}?format=csv`,
                  `prediccion_${meteoId}.csv`
                )}
              >
                Descargar predicción {meteoId}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}